import React from 'react';
import { isValidAnnouncement } from '@helpCenter/util/login-announcements/login-announcements';
import { getCloudId, getHelpCenterAri } from '@helpCenter/util/meta';
import { ScreenName } from '@helpCenter/view/common/constants';
import { sanitize } from 'dompurify';
import { di } from 'react-magnetic-di';
import { useLazyLoadQuery, graphql } from 'react-relay';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import * as grid from 'view/styles/grid';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { loginAnnouncementsCardQuery } from './__generated__/loginAnnouncementsCardQuery.graphql';

// Since LoginAnnouncement epic is made for home page which get messages as plain text, we can't use this epic for our use case, hence we are using useLazyLoadQuery to get the data using graphql query
const LoginAnnouncement = () => {
    di(useLazyLoadQuery);
    const data = useLazyLoadQuery<loginAnnouncementsCardQuery>(
        graphql`
            query loginAnnouncementsCardQuery($cloudId: ID!, $helpCenterAri: ID!) {
                helpCenter(cloudId: $cloudId) {
                    helpCenterById(helpCenterAri: $helpCenterAri) @optIn(to: "HelpCenterAggBeta") {
                        ... on HelpCenter {
                            __typename
                            announcements {
                                loginAnnouncements {
                                    name
                                    description
                                    descriptionType
                                }
                            }
                        }
                        ... on QueryError {
                            __typename
                        }
                    }
                }
            }
        `,
        {
            helpCenterAri: getHelpCenterAri(),
            cloudId: getCloudId(),
        }
    );

    const responseTypeName = data.helpCenter?.helpCenterById?.__typename;
    if (responseTypeName !== 'HelpCenter') {
        // not displaying anything if the response is not of type HelpCenter
        return null;
    }

    // As confirmed by the backend team, there will be only one login announcement for now, hence using the first element
    const announcementDetails = data.helpCenter?.helpCenterById?.announcements?.loginAnnouncements?.[0];
    const loginAnnouncementSubject = announcementDetails?.name ?? '';
    const loginAnnouncementMessage = announcementDetails?.description ?? '';
    const loginAnnouncementDescriptionType = announcementDetails?.descriptionType ?? '';
    const isAnnouncementValid = isValidAnnouncement(loginAnnouncementSubject, loginAnnouncementMessage);

    if (!isAnnouncementValid) {
        // not displaying anything if the announcement is not valid or the description type is not WIKI_MARKUP
        return null;
    }

    return (
        <Box xcss={loginAnnouncementContainer}>
            {loginAnnouncementSubject ? <Heading level="h500">{loginAnnouncementSubject}</Heading> : null}
            {loginAnnouncementMessage && loginAnnouncementDescriptionType === 'WIKI_MARKUP' ? (
                <Box
                    xcss={loginAnnouncementBody}
                    dangerouslySetInnerHTML={{ __html: sanitize(loginAnnouncementMessage) }}
                />
            ) : null}
        </Box>
    );
};

const LoginAnnouncementLoader = () => {
    return (
        <ErrorBoundary packageName={ScreenName.LOGIN} id={'login-announcements'}>
            <LoginAnnouncement />
        </ErrorBoundary>
    );
};

export default LoginAnnouncementLoader;

const loginAnnouncementContainer = xcss({
    marginTop: token('space.300', '24px'),
    paddingBottom: token('space.200', '16px'),
    borderBottom: `1px solid ${token('color.border', 'rgba(9, 30, 66, 0.14)')}`,
});

const loginAnnouncementBody = xcss({
    marginTop: token('space.100', '8px'),
    maxWidth: grid.multiple(42).px,
    overflowWrap: 'break-word',
});
